// src/features/apps/AppBuildRequestUtils.ts
//
// Pure rules of the holder-requested build (the Status tab's "Build the app"
// card, 3.34.0; owner ruling 2026-09-23: the account holder — customer or
// reseller — may ask the farm for a build). The factory stays the authority:
// its POST /v1/builds is idempotent (a re-request while a build is in flight
// answers the same row), admission-gated (unmet prerequisites come back as
// named 422 problems) and, since appfactory ADR-0033, input-aware (a request
// whose inputs match the build the store has is answered with that row and
// `unchanged`). These helpers only decide what the card SAYS before the click
// and how the farm's answer or refusal reads after it (3.34.1: the built-and-
// waiting and live states, found on the owner's first walkthrough).

import {
  FACTORY_ACTIVE_BUILD_STATUSES,
  FACTORY_BUILD_REFUSALS,
  FACTORY_BUILD_STATUS_LABELS,
  FACTORY_RESTING_BUILD_STATUSES,
  FACTORY_STORE_BOUND_SUBMISSION_STATES,
} from '@/lib/constants/appFactory';
import type { FactoryBuild, FactoryRelease, FactorySubmission } from '@/lib/types/AppFactory';
import type { ApiError } from '@/shared/api/ApiError';
import { AppFactoryUtils } from './AppFactoryUtils';

/**
 * The build of one release the store has or will get: resting (built, its
 * checks passed, waiting for a submission or the holder) or with a submission
 * a store holds or serves. `submissionState` is null while the row rests
 * with no submission yet.
 */
export interface StoreBoundBuild {
  build: FactoryBuild;
  submissionState: string | null;
}

export interface BuildButtonInput {
  /** The parent app is Active. */
  appActive: boolean;
  /** The platform's own row (roku / app_cfg_platform) is Active. */
  platformActive: boolean;
  /** The app has its address (app_cfg_web) — builds fail closed without it. */
  hasAddress: boolean;
  /** The release the button would build; null = the feed lists none for the platform. */
  release: FactoryRelease | null;
  /** The platform's build the farm still owns, if any. */
  activeBuild: FactoryBuild | null;
  /** The release's build the store has or will get, if any (storeBoundBuild). */
  storeBound: StoreBoundBuild | null;
  /** The Farm chip is stale (no worker heartbeat lately). */
  farmStale: boolean;
  /**
   * The factory's unmet prerequisites as customer sentences (build readiness,
   * 3.34.5: BuildReadinessUtils.unmetSentences); empty = everything holds,
   * null = unknown (an older api or daemon, a failed read) — the button then
   * keeps today's behaviour and the refusal after the click stays the backstop.
   */
  unmet: string[] | null;
}

export interface BuildButtonState {
  disabled: boolean;
  /** The dimmed line under the button: why it is off, or a neutral caption; null = none. */
  reason: string | null;
  /** The orange line under the button (the farm warning); null = none. */
  warning: string | null;
}

/** One toast after a request the farm accepted. */
export interface BuildOutcome {
  title: string;
  message: string;
  color: 'green' | 'blue';
}

/** The card's sentences; `{platform}` / `{semver}` / `{status}` are filled by the caller. */
export const BUILD_BUTTON_REASONS = {
  APP_INACTIVE: 'Activate the app first.',
  PLATFORM_INACTIVE: 'Turn the {platform} app on in Settings first.',
  NO_ADDRESS: "Set up your app's address first.",
  NO_RELEASE: 'No app version is available to build yet.',
  BUILD_ACTIVE: 'A build is in progress ({status}).',
  WAITING_FOR_STORE: 'Version {semver} is built and waiting for the store — see Store Submissions below.',
  LIVE: 'Version {semver} is live. Build again only after changing images or settings.',
  FARM_OFFLINE: 'The build system is offline; the build starts when it returns.',
} as const;

/** The toasts after an accepted request; `{semver}` is filled by the caller. */
export const BUILD_OUTCOMES = {
  QUEUED: {
    title: 'Build queued',
    message: 'It appears in Build History below and refreshes on its own.',
    color: 'green',
  },
  IN_PROGRESS: {
    title: 'Build already in progress',
    message: 'The farm is still working on the last request; it appears in Build History below.',
    color: 'blue',
  },
  ALREADY_BUILT: {
    title: 'Already built',
    message: 'Version {semver} is built and waiting for the store — see Store Submissions below.',
    color: 'blue',
  },
  UNCHANGED: {
    title: 'Nothing changed',
    message:
      'Version {semver} is already built with these images and settings. Approve new images or change settings, then build again.',
    color: 'blue',
  },
} as const satisfies Record<string, BuildOutcome>;

/** How many refusal sentences one notice carries (the rest wait for the next click). */
const REFUSAL_SENTENCES_MAX = 2;

export class AppBuildRequestUtils {
  /**
   * The release the button builds: the newest one in the feed that lists the
   * platform (a release without a platform list — an older daemon — counts for
   * every platform). Newest by `created`, then by id.
   */
  static currentRelease(releases: FactoryRelease[], platform: string): FactoryRelease | null {
    const sorted = [...AppFactoryUtils.releasesForPlatform(releases, platform)].sort((a, b) =>
      a.created === b.created
        ? AppBuildRequestUtils.compareIdDesc(a.shellReleaseId, b.shellReleaseId)
        : a.created < b.created
          ? 1
          : -1,
    );
    return sorted[0] ?? null;
  }

  static isActiveStatus(status: string): boolean {
    return (FACTORY_ACTIVE_BUILD_STATUSES as readonly string[]).includes(status);
  }

  static isRestingStatus(status: string): boolean {
    return (FACTORY_RESTING_BUILD_STATUSES as readonly string[]).includes(status);
  }

  /** The platform's newest build the farm still owns; null = none in flight. */
  static activeBuild(builds: FactoryBuild[], platform: string): FactoryBuild | null {
    return (
      AppFactoryUtils.sortByCreatedDesc(
        AppFactoryUtils.buildsForPlatform(builds, platform).filter((b) =>
          AppBuildRequestUtils.isActiveStatus(b.status),
        ),
      )[0] ?? null
    );
  }

  /** Any build of the app (any platform) the farm still owns — the poll trigger. */
  static hasActiveBuild(builds: FactoryBuild[]): boolean {
    return builds.some((b) => AppBuildRequestUtils.isActiveStatus(b.status));
  }

  /**
   * The release's build the store has or will get, newest first: a row
   * resting at its checks (`preflight_passed` | `awaiting_customer`), or any
   * row — a superseded one included, the store keeps serving it — whose
   * submission on the platform's store is at a store-bound state
   * (`awaiting_customer` | `submitting` | `in_review` | `approved` | `live`).
   * Null = nothing of that release is built for the store.
   */
  static storeBoundBuild(
    builds: FactoryBuild[],
    submissions: FactorySubmission[],
    platform: string,
    store: string,
    releaseId: string,
  ): StoreBoundBuild | null {
    const candidates = AppFactoryUtils.sortByCreatedDesc(
      AppFactoryUtils.buildsForPlatform(builds, platform).filter((b) => b.shellReleaseId === releaseId),
    );
    const storeSubmissions = AppFactoryUtils.sortByCreatedDesc(
      AppFactoryUtils.submissionsForStore(submissions, store),
    );
    for (const build of candidates) {
      const submission = storeSubmissions.find((s) => s.appBuild2Id === build.appBuild2Id) ?? null;
      const bound =
        submission != null &&
        (FACTORY_STORE_BOUND_SUBMISSION_STATES as readonly string[]).includes(submission.state);
      if (bound) return { build, submissionState: submission!.state };
      if (submission == null && AppBuildRequestUtils.isRestingStatus(build.status)) {
        return { build, submissionState: null };
      }
    }
    return null;
  }

  /**
   * The button's state, first reason wins: the app, the platform row, the
   * address, a release to build, a build already in flight, a build waiting
   * for the store (off: the store gets that one first), a live build (on, with
   * the caption — the farm answers "nothing changed" unless images or settings
   * moved), then (3.34.5) a prerequisite the factory would refuse — off, with
   * the first unmet item's sentence, the same words as the Readiness card's row.
   * A stale farm never disables the button — builds queue honestly and start
   * when it returns — it only adds the warning line.
   */
  static buttonState(input: BuildButtonInput): BuildButtonState {
    const off = (reason: string): BuildButtonState => ({ disabled: true, reason, warning: null });
    if (!input.appActive) return off(BUILD_BUTTON_REASONS.APP_INACTIVE);
    if (!input.platformActive) return off(BUILD_BUTTON_REASONS.PLATFORM_INACTIVE);
    if (!input.hasAddress) return off(BUILD_BUTTON_REASONS.NO_ADDRESS);
    if (!input.release) return off(BUILD_BUTTON_REASONS.NO_RELEASE);
    if (input.activeBuild) {
      const label = FACTORY_BUILD_STATUS_LABELS[input.activeBuild.status] ?? input.activeBuild.status;
      return off(BUILD_BUTTON_REASONS.BUILD_ACTIVE.replace('{status}', label));
    }
    const semver = input.release.semver;
    if (input.storeBound && input.storeBound.submissionState !== 'live') {
      return off(BUILD_BUTTON_REASONS.WAITING_FOR_STORE.replace('{semver}', semver));
    }
    if (input.unmet && input.unmet.length > 0) return off(input.unmet[0]);
    return {
      disabled: false,
      reason: input.storeBound ? BUILD_BUTTON_REASONS.LIVE.replace('{semver}', semver) : null,
      warning: input.farmStale ? BUILD_BUTTON_REASONS.FARM_OFFLINE : null,
    };
  }

  /**
   * The toast for a row the farm answered with: `unchanged` (the store's
   * build, nothing to do), a row already listed (in flight, or resting —
   * "already built"), or a new row (queued). `semver` is the requested
   * release's.
   */
  static outcome(row: FactoryBuild, knownIds: ReadonlySet<string>, semver: string): BuildOutcome {
    const fill = (o: BuildOutcome): BuildOutcome => ({ ...o, message: o.message.replace('{semver}', semver) });
    if (row.unchanged) return fill(BUILD_OUTCOMES.UNCHANGED);
    if (!knownIds.has(row.appBuild2Id)) return fill(BUILD_OUTCOMES.QUEUED);
    if (AppBuildRequestUtils.isActiveStatus(row.status)) return fill(BUILD_OUTCOMES.IN_PROGRESS);
    return fill(BUILD_OUTCOMES.ALREADY_BUILT);
  }

  /**
   * A refused request in the customer's words. A 422 with named problems
   * yields the first two sentences (a known token's sentence, else the
   * factory's own message for that problem); a top-level known code yields its
   * sentence; anything else (a 400, the gateway's bounded 5xx, the network)
   * keeps the error's message.
   */
  static refusalSentence(err: ApiError, platformLabel: string, supportContact: string): string {
    const fill = (s: string) =>
      s.replace(/\{platform\}/g, platformLabel).replace(/\{supportContact\}/g, supportContact);
    if (err.httpStatusCode === 422 && err.problems && err.problems.length > 0) {
      return err.problems
        .slice(0, REFUSAL_SENTENCES_MAX)
        .map((p) => fill(FACTORY_BUILD_REFUSALS[p.field] ?? p.message))
        .join(' ');
    }
    if (err.code && FACTORY_BUILD_REFUSALS[err.code]) return fill(FACTORY_BUILD_REFUSALS[err.code]);
    return err.message;
  }

  /** Snowflake ids are decimal strings: longer is newer, then lexical. */
  private static compareIdDesc(a: string, b: string): number {
    if (a.length !== b.length) return b.length - a.length;
    return a < b ? 1 : a > b ? -1 : 0;
  }
}
